import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Ico } from './ShellIcons';
import nv from './navigator.module.css';
import NavRow from './NavRow';
import { FILTER_FROM, filterRows, recentRows } from './navModel';
import { groupRows } from './navCatalog';

// ============================================================
// Navigator — the section panel under the location pill (CLB-080 S3 · S6), one for every portal, David's v8:
// a header (portal tile · name · section count · what it is · its Home), a "Jump to a section…" filter from
// seven rows, Recent chips, the sections in their groups (Orders: one column per world), a footer with the
// keys. ↑ ↓ move, Enter opens, 1–9 jump, Esc closes and hands focus back to the pill.
// ============================================================

export default function Navigator({ portal, title, rows, columns, world, current, recents, onPick, onClose, pillRef, home, homeLabel, onPin }) {
  const name = title || (portal && portal.name) || '';
  const [q, setQ] = useState('');
  const [pw, setPw] = useState(world || (columns && columns[0] ? columns[0].key : null));
  const root = useRef(null);
  const input = useRef(null);
  const cols = columns && columns.length ? columns : null;
  const here = cols ? (cols.find((c) => c.key === world) || cols[0]) : null;
  const flat = cols ? here.rows : rows;
  const total = cols ? cols.reduce((n, c) => n + c.rows.length, 0) : rows.length;
  const withFilter = total >= FILTER_FROM;
  // rows as drawn (group order): the numbers 1–9 follow the screen, not the raw list
  const shown = useMemo(() => {
    const f = filterRows(flat, q);
    return cols ? f : groupRows(portal && portal.key, f).flatMap((g) => g.rows);
  }, [flat, q, cols, portal]);
  const rec = useMemo(() => (q ? [] : recentRows(flat, recents || [], current)), [flat, recents, current, q]);

  // keep the panel inside the window: it hangs from the pill, so a wide one is moved back left (prototype placePop)
  useLayoutEffect(() => {
    const el = root.current; if (!el) return undefined;
    const place = () => {
      el.style.transform = '';
      const r = el.getBoundingClientRect(); const vw = document.documentElement.clientWidth;
      const over = r.right - (vw - 10);
      const s = over > 0 ? Math.min(over, Math.max(0, r.left - 10)) : 0;
      if (s) el.style.transform = `translateX(-${s}px)`;
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', place); };
  }, [cols]);

  useEffect(() => {
    if (withFilter && input.current) input.current.focus();
    else { const el = root.current && (root.current.querySelector('[data-nav-row][aria-checked="true"]') || root.current.querySelector('[data-nav-row]')); if (el) el.focus(); }
  }, [withFilter]);

  const leave = () => { onClose(); if (pillRef && pillRef.current) pillRef.current.focus(); };
  const pick = (r, worldKey) => { onPick(r.key, worldKey); onClose(); if (pillRef && pillRef.current) pillRef.current.focus(); };
  const numOf = (r) => { const i = shown.indexOf(r); return i > -1 && i < 9 ? i + 1 : 0; };
  const rowsEls = () => Array.from(root.current ? root.current.querySelectorAll('[data-nav-row]') : []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); leave(); return; }
    const typing = e.target === input.current;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const list = rowsEls();
      if (!list.length) return;
      const i = list.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i <= 0 ? list.length - 1 : i - 1);
      list[next].focus();
      return;
    }
    if (e.key === 'Enter' && typing && shown[0]) { e.preventDefault(); pick(shown[0], cols ? here.key : undefined); return; }
    if ((!typing || !q) && !e.altKey && !e.ctrlKey && !e.metaKey && /^[1-9]$/.test(e.key) && shown[Number(e.key) - 1]) { e.preventDefault(); pick(shown[Number(e.key) - 1], cols ? here.key : undefined); }
  };

  const rowEl = (r, worldKey, numbered) => (
    <NavRow key={`${worldKey || ''}${r.key}`} r={r} num={numbered ? numOf(r) : 0} on={r.key === current && (!cols || worldKey === world)} isHome={home === r.key} portalName={name}
      onPick={(x) => pick(x, worldKey)} onPin={onPin} />
  );

  return (
    <div ref={root} className={`${nv.nav} ${cols ? nv.wide : ''}`} role="menu" aria-label={`${name} sections`} data-shell-pop data-test="shell-section-menu" onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
      <div className={nv.nvh}>
        {portal && portal.icon ? <span className={nv.tile}><Ico name={portal.icon} /></span> : null}
        <div className={nv.nvtx}>
          <div className={nv.nvt}>{name}<span className={nv.nvc}>{total} sections</span></div>
          {portal && portal.desc ? <div className={nv.nvd}>{portal.desc}</div> : null}
        </div>
        {homeLabel ? <span className={nv.hm} title={`Clicking ${name} in the top bar opens its Home`}><Ico name="home" />Home <b>{homeLabel}</b></span> : null}
      </div>
      {withFilter && (
        <div className={nv.nvf}>
          <Ico name="search" />
          <input ref={input} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to a section…" autoComplete="off" spellCheck="false" aria-label="Filter sections" data-test="shell-section-find" />
          <kbd className={nv.fkbd}>type · 1–9</kbd>
        </div>
      )}
      <div className={nv.nvbody}>
        {rec.length > 0 && (
          <div className={nv.nrc} role="group" aria-label="Recent" data-test="shell-section-recent">
            <span className={nv.lb}>Recent</span>
            {rec.map((r) => <button key={`r-${r.key}`} type="button" role="menuitem" data-nav-row className={nv.rc} onClick={() => pick(r, cols ? here.key : undefined)}>{r.label}</button>)}
          </div>
        )}
        {cols ? (
          <div className={nv.nvcols}>
            <div className={nv.wtabs} role="tablist" aria-label="Worlds">
              {cols.map((c) => <button key={c.key} type="button" role="tab" aria-selected={c.key === pw} className={`${nv.wt} ${c.key === pw ? nv.wtOn : ''}`} onClick={() => setPw(c.key)}><Ico name={c.icon} />{c.label}</button>)}
            </div>
            {cols.map((c) => {
              const list = c.key === here.key ? shown : filterRows(c.rows, q);
              return (
                <div key={c.key} className={`${nv.ncol} ${c.key === here.key ? nv.here : ''} ${c.key === pw ? nv.pv : ''}`}>
                  <div className={nv.nwh}><span className={nv.wi}><Ico name={c.icon} /></span><span>{c.label}</span>{c.key === here.key ? <span className={`${nv.tg} ${nv.tgHome}`}>You are here</span> : null}<small>{list.length}</small></div>
                  {list.map((r) => rowEl(r, c.key, c.key === here.key))}
                  {list.length === 0 ? <div className={nv.none}>No match</div> : null}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {groupRows(portal ? portal.key : '', shown).map((g) => (
              <div key={g.name || 'all'} className={nv.ng} role="group" aria-label={g.name || undefined}>
                {g.name ? <div className={nv.ngh}>{g.name}</div> : null}
                {g.rows.map((r) => rowEl(r, undefined, true))}
              </div>
            ))}
            {shown.length === 0 && <div className={nv.none}>No section matches “{q}”. Try a shorter word.</div>}
          </>
        )}
      </div>
      <div className={nv.nvft}>
        <span className={nv.lv}><i />Live</span>
        <span className={nv.kb}><kbd>↑↓</kbd> move · <kbd>↵</kbd> open · <kbd>1–9</kbd> jump · <kbd>Esc</kbd> close</span>
      </div>
    </div>
  );
}
